import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  AuthResponse,
  ChatResponse,
  VoiceResponse,
  ImageResponse,
  HistoryResponse,
  UploadResponse,
  AdminUser,
  AdminAnalytics,
  StripeCheckoutResponse,
  StripePortalResponse,
  ApiResponse,
  User,
  Usage,
  PlanLimits,
  PlanType
} from '../types';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const customToken = localStorage.getItem('auth_token');
    const token = customToken 
    console.log('API Request:', config.url, 'Token:', token);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    console.log('API Error:', error.config?.url, error.response?.status, error.response?.data);
    
    // Don't auto-signout for password change errors (401 on /auth/change-password)
    if (error.response?.status === 401 && error.config?.url?.includes('/auth/change-password')) {
      console.log('Password change failed - not signing out user');
      return Promise.reject(error);
    }
    
    // Don't auto-signout for login errors (401 on /auth/login)
    if (error.response?.status === 401 && error.config?.url?.includes('/auth/login')) {
      console.log('Login failed - not signing out user');
      return Promise.reject(error);
    }
    
    // Don't auto-signout for registration errors (400/401 on /auth/register)
    if ((error.response?.status === 400 || error.response?.status === 401) && error.config?.url?.includes('/auth/register')) {
      console.log('Registration failed - not signing out user');
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401) {
      console.log('401 error - clearing tokens and reloading');
      // Token expired or invalid - clear both token types
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // Custom password-based login
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  // Custom password-based registration
  register: async (email: string, password: string, name: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', { email, password, name });
    return response.data;
  },

  // Verify custom JWT token
  verifyToken: async (_token?: string): Promise<AuthResponse> => {
    // Always use auth_token for custom verify
    const customToken = localStorage.getItem('auth_token');
    if (!customToken) throw new Error('No custom auth token found');
    const response = await api.post('/auth/verify-token', { token: customToken });
    return response.data;
  },

  // Get user profile
  getProfile: async (): Promise<AuthResponse> => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  // Update user profile
  updateProfile: async (data: { name?: string; avatar_url?: string }): Promise<ApiResponse<User>> => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  // Forgot password
  forgotPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  // Reset password
  resetPassword: async (token: string, password: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },

  // Change password (requires current password)
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.put('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },
// src/api/authAPI.ts
  deleteAccount: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete('/auth/profile');
    console.log('🗑️ deleteAccount API response:', response.data);
    return response.data;
  },
};


export const chatAPI = {
  // Send text message (supports both authenticated and guest users)
  sendMessage: async (payload: { 
    message: string; 
    guest_id?: string | null; 
    language?: string | null; 
    user_type?: "guest" | "user" | "admin" | "super-admin" | "basic" | "premium" | "ultimate";
  }): Promise<ChatResponse> => {
    
    // ✅ Normalize user_type
    const normalizedPayload = {
      ...payload,
      user_type: (payload.user_type === "super-admin" || payload.user_type === "admin")
        ? "premium"
        : payload.user_type,
    };

    console.log("📤 Payload sending to /chat API:", normalizedPayload);

    const response = await api.post('/chat', normalizedPayload);

    console.log("✅ sendMessage API response:", response.data);
    return response.data;
  },



  // Get usage info
  getUsage: async (): Promise<{ usage: Usage; limits: PlanLimits; plan_type: string }> => {
    const response = await api.get('/chat/usage');
    return response.data;
  },

  // Claim guest conversation after login
  claimGuestConversation: async (guest_id: string): Promise<{ success: boolean; message: string; claimedCount: number }> => {
    console.log('claimGuestConversation API call:', { guest_id });
    const response = await api.post('/chat/claim-guest', { guest_id });
    console.log('claimGuestConversation API response:', response.data);
    return response.data;
  },
};

// Voice API
// src/api/voice.ts
export const voiceAPI = {
  async sendVoice(file: File, guestId?: string, language?: string) {
    const fd = new FormData();
    fd.append('audio', file, file.name);

    if (guestId) fd.append('guest_id', guestId);
    if (language) fd.append('language', language);
    console.log('📤 Payload sending to /voice API:', fd);

    const res = await api.post('/voice', fd, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const raw = res.data;

    return {
      transcription: raw.transcription,
      botResponse: raw.botResponse ?? raw.bot_response,
      audioUrl: raw.audioUrl ?? raw.audio_url ?? null,
      timestamp: raw.timestamp ?? new Date().toISOString(),
      usage: raw.usage,
      limits: raw.limits,
      guestInfo: raw.guestInfo ?? raw.guest_info,
    };
  },
};

// Image API
export const imageAPI = {
  // Send image for analysis
  sendImage: async (
    imageFile: File,
    description: string,
    guest_id?: string,
    language?: string // ✅ add language field
  ): Promise<ImageResponse> => {
    const formData = new FormData();
    formData.append('image_file', imageFile);
    formData.append('description', description);

    if (guest_id) formData.append('guest_id', guest_id);
    if (language) formData.append('language', language); // ✅ add language

    const response = await api.post('/image/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes timeout for image processing
    });
    return response.data;
  },

  // Get image usage
  getImageUsage: async (): Promise<{
    image_uploads: number;
    image_limit: number;
    plan_type: string;
  }> => {
    const response = await api.get('/image/usage');
    return response.data;
  },
};


// History API
export const historyAPI = {
  // Get chat history
  getHistory: async (params?: {
    page?: number;
    limit?: number;
    type?: string;
    startDate?: string;
    endDate?: string;
    guest_id?: string;
  }): Promise<HistoryResponse> => {
    const response = await api.get('/history', { params });
    return response.data;
  },

  // Get history stats
  getStats: async (): Promise<{ stats: any }> => {
    const response = await api.get('/history/stats');
    return response.data;
  },

  // Delete specific message
  deleteMessage: async (messageId: number): Promise<ApiResponse> => {
    const response = await api.delete(`/history/${messageId}`);
    return response.data;
  },

  // Clear all history
  clearHistory: async (): Promise<ApiResponse> => {
    const response = await api.delete('/history');
    return response.data;
  },
};

// Upload API
export const uploadAPI = {
  // Upload image
  uploadImage: async (imageFile: File, description?: string): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append('image', imageFile);
    if (description) {
      formData.append('description', description);
    }
    
    const response = await api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get uploaded images
  getImages: async (params?: { page?: number; limit?: number }): Promise<{
    images: any[];
    pagination: any;
  }> => {
    const response = await api.get('/upload/images', { params });
    return response.data;
  },

  // Delete upload
  deleteUpload: async (uploadId: number): Promise<ApiResponse> => {
    const response = await api.delete(`/upload/${uploadId}`);
    return response.data;
  },
};


// interface StripeCheckoutResponse {
//   url?: string;
//   success?: boolean;
//   subscription?: any;
//   error?: string;
// }

// Stripe API
export const stripeAPI = {
  // Create checkout session
  createCheckoutSession: async (plan: PlanType): Promise<StripeCheckoutResponse> => {
    const response = await api.post('/stripe/create-checkout-session', { plan });
    return response.data;
  },

  // Create portal session
  createPortalSession: async (): Promise<StripePortalResponse> => {
    const response = await api.post('/stripe/create-portal-session');
    return response.data;
  },

  // Get subscription info
  getSubscription: async (): Promise<{ subscription: any }> => {
    const response = await api.get('/stripe/subscription');
    return response.data;
  },

  // Get billing history
  getBillingHistory: async (): Promise<{ success: boolean; history: Array<{
    id: string;
    date: Date;
    amount: number;
    currency: string;
    plan: string;
    invoicePdf: string;
    status: string;
    hostedInvoiceUrl: string;
  }> }> => {
    const response = await api.get('/stripe/history');
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  // Get all users
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    plan?: string;
  }): Promise<{
    users: AdminUser[];
    pagination: any;
    filters: any;
  }> => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  // Get analytics
  getAnalytics: async (): Promise<{ analytics: AdminAnalytics }> => {
    const response = await api.get('/admin/analytics');
    return response.data;
  },

  // Get specific user details
  getUser: async (userId: number): Promise<{
    user: any;
    subscriptions: any[];
    recentMessages: any[];
    recentUploads: any[];
  }> => {
    const response = await api.get(`/admin/user/${userId}`);
    return response.data;
  },

  // Update user (admin only)
  updateUser: async (userId: number, data: { plan_type?: string; name?: string }): Promise<ApiResponse<User>> => {
    const response = await api.put(`/admin/user/${userId}`, data);
    return response.data;
  },

  // Get system health
  getSystemHealth: async (): Promise<{ systemHealth: any }> => {
    const response = await api.get('/admin/system-health');
    return response.data;
  },
};

export default api; 